import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, DollarSign, CreditCard, FileText, Heart, BarChart3, Shield, BookOpen, MessageCircle, Zap } from 'lucide-react';

const FinanceOptions = () => {
  const financeOptions = [
    {
      icon: <Zap className="w-12 h-12 text-accent-500" />,
      title: "Fast Business Cash",
      description: "Fast Business Cash provides quick access to working capital designed to help businesses manage short-term financial needs",
      image: "fast-business-cash",
      path: "/finance/fast-business-cash",
    },
    {
      icon: <CreditCard className="w-12 h-12 text-accent-500" />,
      title: "Flexible Line of Credit",
      description: "A flexible line of credit allows businesses to borrow funds when needed and only pay interest on the amount used",
      image: "line-of-credit",
      path: "/finance",
    },
    {
      icon: <FileText className="w-12 h-12 text-accent-500" />,
      title: "Invoice Finance",
      description: "Invoice finance allows businesses to unlock cash tied up in unpaid invoices and improve cash flow",
      image: "invoice-finance",
      path: "/finance/invoice-finance",
    },
    {
      icon: <Heart className="w-12 h-12 text-accent-500" />,
      title: "Responsible Finance",
      description: "Responsible finance provides funding for businesses, social enterprises, and organisations that may not qualify for traditional bank loans",
      image: "responsible-finance",
      path: "/finance",
    },
    {
      icon: <DollarSign className="w-12 h-12 text-accent-500" />,
      title: "Merchant Cash Advance",
      description: "Merchant funding allows businesses to receive capital in exchange for a percentage of future card sales",
      image: "merchant-cash-advance",
      path: "/finance",
    },
    {
      icon: <Shield className="w-12 h-12 text-accent-500" />,
      title: "Unsecured Business Loan",
      description: "Unsecured loans provide business funding without requiring assets as collateral",
      image: "unsecured-business-loan",
      path: "/finance/unsecured-loans",
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-accent-500" />,
      title: "Secured Business Loan",
      description: "Secured loans allow businesses to borrow larger amounts by providing assets such as property or equipment as security",
      image: "secured-business-loan",
      path: "/finance",
    },
  ];

  const resources = [
    {
      icon: <BookOpen className="w-8 h-8 text-primary-600" />,
      title: "SME Funding Guide",
      description: "Understand the different types of funding available for small and medium-sized businesses",
      button: "Read Guide",
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-primary-600" />,
      title: "Case Studies",
      description: "See how 1ST TRUST FINANCES has helped businesses secure the funding they need",
      button: "View Case Studies",
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-primary-600" />,
      title: "Speak to a Funding Specialist",
      description: "Our funding experts can help you choose the right finance option for your business",
      button: "Contact Us",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                Business{' '}
                <span className="gradient-text">Finance Options</span>
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                Explore flexible funding solutions designed to support businesses at every stage of growth.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Businesses today have access to a wide range of financing solutions that can help manage cash flow, support expansion, and unlock new opportunities.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At 1ST TRUST FINANCES, we help business owners understand the different finance options available and connect them with lenders offering competitive funding solutions.
              </p>
              <Link to="/finance" className="btn-primary inline-flex items-center">
                Explore Funding Options
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="financeOptionsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#financeOptionsGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <BarChart3 className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="introFinanceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#introFinanceGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Understanding Your Business Finance Options
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Every business has unique funding needs depending on its size, industry, and growth stage.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Some businesses need short-term working capital, while others require long-term funding for expansion.
              </p>
              <p className="text-gray-600 leading-relaxed">
                1ST TRUST FINANCES helps businesses explore the financing options available so they can choose the solution that best supports their goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Finance Options Grid */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Explore Our Finance Solutions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find the perfect funding solution for your business needs.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {financeOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
              >
                <div className="mb-6">{option.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {option.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                  {option.description}
                </p>
                <Link to={option.path} className="btn-secondary w-full">
                  Learn More
                  <ArrowRight className="inline ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Funding Guidance Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Find the Right Finance for Your Business
              </h2>
              <p className="text-gray-600 leading-relaxed">
                With so many funding solutions available today, choosing the right one can sometimes be challenging.
              </p>
              <p className="text-gray-600 leading-relaxed">
                1ST TRUST FINANCES helps businesses navigate the funding landscape by connecting them with multiple lenders and providing clear information about available finance options.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our goal is to make the funding process simple, transparent, and accessible for businesses of all sizes.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="guidanceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#guidanceGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Resources
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Helpful resources to guide your funding journey.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{resource.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {resource.description}
                </p>
                <button className="btn-secondary w-full">
                  {resource.button}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid5" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid5)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Compare Business Funding Options Today
            </h2>
            <p className="text-xl text-gray-300 mb-4">
              1ST TRUST FINANCES connects businesses with multiple lenders offering a wide range of funding solutions.
            </p>
            <p className="text-gray-300 mb-8">
              Submit one funding request and receive personalised funding offers from lenders ready to support your business.
            </p>
            <Link to="/apply-for-funding" className="inline-block bg-white text-primary-600 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Get Funding Quotes
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FinanceOptions;
